class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      CartRewardBar.sync();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// ==============================
// CART REWARD BAR — Brota
// 1 tramo: barrita Matcha & Greens a $50.000
// ==============================
const CartRewardBar = {

  TIERS: [
    {
      threshold: 5000000,        // $50.000 en centavos
      variantId: 42652082471130, // Barrita Matcha & Greens
      quantity: 1,
      label: '1 barrita de regalo',
    },
  ],

  _processing: false,

  formatMoney(cents) {
    return '$' + (cents / 100).toLocaleString('es-CL', { maximumFractionDigits: 0 });
  },

  isGiftItem(item) {
    const knownIds = this.TIERS.map(t => t.variantId);
    return (item.properties && item.properties._gift === 'true') ||
           knownIds.includes(item.variant_id);
  },

  currentTier(totalCents) {
    let reached = null;
    for (const tier of this.TIERS) {
      if (totalCents >= tier.threshold) reached = tier;
    }
    return reached;
  },

  nextTier(totalCents) {
    return this.TIERS.find(t => totalCents < t.threshold) || null;
  },

  async sync() {
    try {
      const resp = await fetch('/cart.js');
      const cart = await resp.json();

      const giftItems   = cart.items.filter(i => this.isGiftItem(i));
      const normalItems = cart.items.filter(i => !this.isGiftItem(i));
      const totalCents  = normalItems.reduce((sum, i) => sum + i.final_line_price, 0);

      this.updateUI(totalCents);

      if (!this._processing) {
        await this.manageGift(totalCents, giftItems);
      }
    } catch(e) {
      console.warn('[CartRewardBar] sync error:', e);
    }
  },

  updateUI(totalCents) {
    const fill = document.getElementById('CartDrawer-RewardFill');
    const text = document.getElementById('CartDrawer-RewardText');
    if (!fill || !text) return;

    const threshold = this.TIERS[0].threshold;
    const pct = Math.min((totalCents / threshold) * 100, 100);
    fill.style.width = pct + '%';

    const tier = this.currentTier(totalCents);

    if (tier) {
      text.innerHTML = '🎁 ¡Agregamos ' + tier.label + ' a tu pedido!';
      text.classList.add('is-reached');
    } else {
      text.innerHTML = '🎁 Compra <strong>' + this.formatMoney(threshold - totalCents) + '</strong> más para obtener ' + this.TIERS[0].label;
      text.classList.remove('is-reached');
    }

    const dot = document.getElementById('CartDrawer-RewardDot-0');
    if (dot) {
      if (totalCents >= threshold) dot.classList.add('is-reached');
      else dot.classList.remove('is-reached');
    }
  },

  async manageGift(totalCents, giftItems) {
    this._processing = true;
    try {
      const tier = this.currentTier(totalCents);
      let needsRefresh = false;

      if (!tier) {
        // Sin tier: quitar todos los regalos
        for (const item of giftItems) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 })
          });
          needsRefresh = true;
        }
      } else {
        const correctItems = giftItems.filter(i => i.variant_id === tier.variantId);
        const wrongItems   = giftItems.filter(i => i.variant_id !== tier.variantId);

        for (const item of wrongItems) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 })
          });
          needsRefresh = true;
        }

        if (correctItems.length === 0) {
          const r = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: tier.variantId,
              quantity: tier.quantity,
              properties: { _gift: 'true' }
            })
          });
          if (r.ok) needsRefresh = true;

        } else if (correctItems.length > 1) {
          for (let i = 1; i < correctItems.length; i++) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: correctItems[i].key, quantity: 0 })
            });
            needsRefresh = true;
          }
        }
      }

      if (needsRefresh) await this.refreshDrawer();

    } catch(e) {
      console.warn('[CartRewardBar] manageGift error:', e);
    } finally {
      this._processing = false;
    }
  },

  async refreshDrawer() {
    try {
      const resp = await fetch('/?section_id=cart-drawer', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!resp.ok) return;

      const html     = await resp.text();
      const parsed   = new DOMParser().parseFromString(html, 'text/html');
      const newInner = parsed.querySelector('.drawer__inner');
      const target   = document.querySelector('#CartDrawer .drawer__inner');

      if (newInner && target) {
        target.innerHTML = newInner.innerHTML;
        const overlay = document.getElementById('CartDrawer-Overlay');
        const drawer  = document.querySelector('cart-drawer');
        if (overlay && drawer) overlay.addEventListener('click', drawer.close.bind(drawer));
        const cartResp    = await fetch('/cart.js');
        const cart        = await cartResp.json();
        const normalItems = cart.items.filter(i => !this.isGiftItem(i));
        const totalCents  = normalItems.reduce((sum, i) => sum + i.final_line_price, 0);
        this.updateUI(totalCents);
        this.refreshCartIcon();
      }
    } catch(e) {
      console.warn('[CartRewardBar] refreshDrawer error:', e);
    }
  },

  async refreshCartIcon() {
    try {
      const resp = await fetch('/?section_id=cart-icon-bubble', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!resp.ok) return;
      const html      = await resp.text();
      const parsed    = new DOMParser().parseFromString(html, 'text/html');
      const newBubble = parsed.querySelector('.shopify-section');
      const target    = document.getElementById('cart-icon-bubble');
      if (newBubble && target) target.innerHTML = newBubble.innerHTML;
    } catch(e) { /* silencioso */ }
  },
};

// Patch renderContents para disparar sync al actualizar el carrito
(function() {
  let _patched = false;
  const _tryPatch = () => {
    const proto = customElements.get('cart-drawer')?.prototype;
    if (!proto || _patched) return;
    const _orig = proto.renderContents;
    proto.renderContents = function(parsedState) {
      _orig.call(this, parsedState);
      setTimeout(() => CartRewardBar.sync(), 400);
    };
    _patched = true;
  };
  if (customElements.get('cart-drawer')) {
    _tryPatch();
  } else {
    customElements.whenDefined('cart-drawer').then(_tryPatch);
  }
})();

document.addEventListener('DOMContentLoaded', () => {
  CartRewardBar.sync();

  // Polling: revisar carrito cada 1.5s cuando el drawer está abierto
  setInterval(() => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.classList.contains('active')) {
      CartRewardBar.sync();
    }
  }, 1500);
});